/**
 * QHS Dialog — v3 modal shell with header / body / footer slots.
 *
 * Wraps the legacy `_legacy/components/Shared/Modal` (MUI Dialog
 * under the hood) and papers over a few cross-cutting issues so
 * individual dialogs can stop duplicating the same fix each time:
 *
 *   - Legacy Modal always renders an empty `titlesBox` with padding
 *     before `children`; we hide it so our Header sits flush.
 *   - Paper is a flex-column capped to the viewport: Header and
 *     Footer stay pinned, Body scrolls when content overflows on
 *     short viewports.
 *   - Body / Footer share hairline separators that match the
 *     design-system dialog spec (header-below rule + footer-above
 *     rule when both slots are present).
 *
 *   <Dialog
 *     open={open}
 *     onClose={close}
 *     header={<>...title / icon / description...</>}
 *     footer={<>...cancel + confirm...</>}
 *     maxWidth={520}
 *   >
 *     {bodyContent}
 *   </Dialog>
 */
import Modal from '_legacy/components/Shared/Modal';
import { styled } from '../styled';

const buildPaperSx = ({ maxWidth, minWidth, footerBg, fixedHeight }) => ({
  '& .MuiDialog-paper': {
    width: '100%',
    minWidth: minWidth ?? 420,
    maxWidth: maxWidth ?? 520,
    maxHeight: 'calc(100vh - 40px)',
    // When `fixedHeight` is set, pin the paper to maxHeight so the
    // modal doesn't jump between rows of content (e.g. a drill-down
    // whose body table can be 1 or 50 rows).
    ...(fixedHeight && { height: 'calc(100vh - 40px)' }),
    margin: '20px',
    borderRadius: 'var(--qhs-r-2xl)',
    boxShadow: 'var(--qhs-sh-4)',
    background: 'var(--qhs-surface)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    // Legacy Modal always renders an empty `titlesBox` wrapper with
    // theme.spacing(3, 6) padding before `children` — hide it so
    // our Header sits flush against the top of the paper.
    '& > div[class*="titlesBox"]': { display: 'none' },
    // Paper's `children` wrapper takes the remaining height so our
    // Body flex: 1 / overflow-y works end-to-end.
    '& > [data-qhs-dialog-stack]': {
      flex: 1,
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
    },
    ...(footerBg && {
      '& [data-qhs-dialog-footer]': { background: footerBg },
    }),
  },
});

const Stack = styled('div')({
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
});

// Header gets a bottom hairline ONLY when there's a body below it —
// otherwise the rule lands on top of the footer's own top hairline
// and reads as two stacked lines around an empty zone (which is what
// happens for a "title + description + confirm/cancel" dialog with
// no body content).
const HeaderSlot = styled('div', {
  shouldForwardProp: prop => prop !== '$divider',
})(({ $divider }) => ({
  flexShrink: 0,
  borderBottom: $divider ? '1px solid var(--qhs-border)' : 'none',
}));

const BodySlot = styled('div')({
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
});

// Footer drops its top hairline when there's no body — see
// HeaderSlot's note above. The two slots touch directly with no rule.
const FooterSlot = styled('div', {
  shouldForwardProp: prop => prop !== '$divider',
})(({ $divider }) => ({
  flexShrink: 0,
  borderTop: $divider ? '1px solid var(--qhs-border)' : 'none',
}));

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {React.ReactNode} [props.header]  Pinned top slot. When
 *   provided, a hairline divider is drawn beneath it.
 * @param {React.ReactNode} [props.footer]  Pinned bottom slot. When
 *   provided, a hairline divider is drawn above it.
 * @param {React.ReactNode} props.children  Scrolling body slot.
 * @param {number} [props.maxWidth=520]
 * @param {number} [props.minWidth=420]
 * @param {string} [props.footerBg]  Optional background colour for
 *   the footer strip (e.g. `var(--qhs-bg)` for form-style dialogs
 *   with a call-to-action footer).
 * @param {string} [props.ariaLabel]
 */
const Dialog = ({
  open,
  onClose,
  header,
  footer,
  footerBg,
  maxWidth,
  minWidth,
  fixedHeight,
  ariaLabel,
  children,
  ...rest
}) => (
  <Modal
    open={open}
    handleClose={onClose}
    sx={buildPaperSx({ maxWidth, minWidth, footerBg, fixedHeight })}
    aria-label={ariaLabel}
    {...rest}
  >
    <Stack data-qhs-dialog-stack>
      {header && <HeaderSlot $divider={!!children}>{header}</HeaderSlot>}
      {children ? <BodySlot>{children}</BodySlot> : null}
      {footer && (
        <FooterSlot $divider={!!children} data-qhs-dialog-footer>
          {footer}
        </FooterSlot>
      )}
    </Stack>
  </Modal>
);

export default Dialog;
